import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import { SidebarComponent } from '../../components/sidebar/sidebar.component';
import { NavbarComponent } from '../../components/navbar/navbar.component';
import { OrderService } from '../../core/services/order.service';
import { Order, OrderStats } from '../../models/order.model';

@Component({
  selector: 'app-order-list',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, SidebarComponent, NavbarComponent],
  template: `
    <app-sidebar></app-sidebar>
    <app-navbar
      pageTitle="Orders"
      pageSubtitle="Manage customer orders, payments & Shiprocket fulfillment"
    ></app-navbar>

    <div class="page-wrapper">
      <main class="main-content animate-in">

        <!-- STAT CARDS -->
        <section class="stats-row">
          <div class="stat-card glass-card gold-border-card">
            <div class="icon-box icon-box-gold icon-box-sm">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/><line x1="1" y1="10" x2="23" y2="10"/>
              </svg>
            </div>
            <div class="stat-info">
              <div class="stat-num text-gold">{{ stats?.totalOrders || 0 }}</div>
              <div class="stat-lbl">Total Orders</div>
            </div>
          </div>

          <div class="stat-card glass-card">
            <div class="icon-box icon-box-sm" style="background:rgba(34,197,94,.1);color:#22c55e;border:1px solid rgba(34,197,94,.2)">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
            <div class="stat-info">
              <div class="stat-num" style="color:#22c55e">₹{{ (stats?.totalRevenue || 0) | number }}</div>
              <div class="stat-lbl">Total Revenue</div>
            </div>
          </div>

          <div class="stat-card glass-card">
            <div class="icon-box icon-box-sm" style="background:rgba(251,191,36,.1);color:#fbbf24;border:1px solid rgba(251,191,36,.2)">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
              </svg>
            </div>
            <div class="stat-info">
              <div class="stat-num" style="color:#fbbf24">{{ stats?.pendingOrders || 0 }}</div>
              <div class="stat-lbl">Pending Orders</div>
            </div>
          </div>

          <div class="stat-card glass-card">
            <div class="icon-box icon-box-sm" style="background:rgba(96,165,250,.1);color:#60a5fa;border:1px solid rgba(96,165,250,.2)">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="3" y="3" width="18" height="18" rx="2"/><line x1="3" y1="9" x2="21" y2="9"/>
              </svg>
            </div>
            <div class="stat-info">
              <div class="stat-num" style="color:#60a5fa">{{ stats?.todayOrders || 0 }}</div>
              <div class="stat-lbl">Today's Orders</div>
            </div>
          </div>
        </section>

        <!-- FILTERS ROW -->
        <section class="glass-card" style="padding:20px 24px;margin-bottom:20px">
          <div style="display:grid;grid-template-columns:1fr auto auto auto;gap:14px;align-items:flex-end">
            <div class="form-group" style="margin-bottom:0">
              <label class="form-label">Search</label>
              <input
                class="form-control"
                [(ngModel)]="searchQuery"
                (ngModelChange)="onSearchChange()"
                placeholder="Order #, customer name, email, phone..."
              />
            </div>
            <div class="form-group" style="margin-bottom:0">
              <label class="form-label">Order Status</label>
              <select class="form-control" [(ngModel)]="statusFilter" (change)="loadOrders()">
                <option value="all">All Statuses</option>
                <option value="placed">Placed</option>
                <option value="confirmed">Confirmed</option>
                <option value="processing">Processing</option>
                <option value="shipped">Shipped</option>
                <option value="delivered">Delivered</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            <div class="form-group" style="margin-bottom:0">
              <label class="form-label">Payment</label>
              <select class="form-control" [(ngModel)]="paymentFilter" (change)="loadOrders()">
                <option value="all">All Payments</option>
                <option value="paid">Paid</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            <button class="btn btn-secondary" (click)="resetFilters()">Reset</button>
          </div>
        </section>

        <!-- ORDERS TABLE -->
        <section class="glass-card table-card">
          <div class="table-header">
            <div class="table-title">
              <span>Orders</span>
              <span class="table-count">{{ totalOrders }}</span>
            </div>
          </div>

          <!-- Loading -->
          <div *ngIf="loading" class="loading-container">
            <div class="loading-spinner"></div>
            <p>Loading orders...</p>
          </div>

          <!-- Empty State -->
          <div *ngIf="!loading && orders.length === 0" class="empty-state">
            <p>No orders found.</p>
          </div>

          <!-- Table -->
          <div class="table-scroll" *ngIf="!loading && orders.length > 0">
            <table class="data-table">
              <thead>
                <tr>
                  <th>Order #</th>
                  <th>Customer</th>
                  <th>Items</th>
                  <th>Total</th>
                  <th>Payment</th>
                  <th>Shiprocket Logistics</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th style="text-align:right">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let order of orders" class="order-table-row">
                  <td>
                    <a [routerLink]="['/orders', order._id]" class="order-number-link">
                      {{ order.orderNumber }}
                    </a>
                  </td>
                  <td>
                    <a [routerLink]="['/orders', order._id]" class="customer-link">
                      <div class="customer-name">{{ order.customer.name }}</div>
                      <div class="customer-meta">{{ order.customer.email }}</div>
                      <div class="customer-meta">{{ order.customer.phone }}</div>
                    </a>
                  </td>
                  <td>
                    <span class="badge badge-grey">
                      {{ order.items.length }} item{{ order.items.length !== 1 ? 's' : '' }}
                    </span>
                  </td>
                  <td>
                    <span class="order-total-val">₹{{ order.total | number }}</span>
                  </td>
                  <td>
                    <span [class]="getPaymentBadgeClass(order.paymentStatus)">
                      {{ order.paymentStatus | titlecase }}
                      <span style="font-size:10px;opacity:.85"> · Prepaid</span>
                    </span>
                  </td>
                  <td>
                    <!-- AWB Generated -->
                    <div *ngIf="order.shiprocket?.awbCode" class="shiprocket-tag-box">
                      <a [routerLink]="['/orders', order._id]" class="badge badge-purple" style="text-decoration:none">
                        🚀 {{ order.shiprocket?.awbCode }}
                      </a>
                      <span *ngIf="order.shiprocket?.courierName" class="courier-sub">
                        {{ order.shiprocket?.courierName }}
                      </span>
                    </div>

                    <!-- Shipment Created but AWB pending -->
                    <a
                      *ngIf="!order.shiprocket?.awbCode && order.shiprocket?.shipmentId"
                      [routerLink]="['/orders', order._id]"
                      class="badge badge-gold"
                      style="text-decoration:none"
                    >
                      📦 #{{ order.shiprocket?.shipmentId }}
                    </a>

                    <!-- Not yet in Shiprocket -->
                    <a
                      *ngIf="!order.shiprocket?.shipmentId"
                      [routerLink]="['/orders', order._id]"
                      class="btn-quick-shiprocket"
                    >
                      + Shiprocket
                    </a>
                  </td>
                  <td>
                    <span [class]="getStatusBadgeClass(order.orderStatus)">
                      {{ order.orderStatus | titlecase }}
                    </span>
                  </td>
                  <td style="font-size:12px;color:var(--text-muted);white-space:nowrap">
                    {{ order.createdAt | date:'dd MMM yy, HH:mm' }}
                  </td>
                  <td style="text-align:right">
                    <div style="display:inline-flex;gap:8px;align-items:center">
                      <a
                        [routerLink]="['/orders', order._id]"
                        class="btn btn-sm btn-secondary view-btn"
                      >
                        <span>View</span>
                        <span>&rarr;</span>
                      </a>
                      <select
                        class="form-control quick-status-select"
                        [(ngModel)]="order.orderStatus"
                        (change)="quickUpdateStatus(order)"
                      >
                        <option value="placed">Placed</option>
                        <option value="confirmed">Confirmed</option>
                        <option value="processing">Processing</option>
                        <option value="shipped">Shipped</option>
                        <option value="delivered">Delivered</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Pagination Footer -->
          <div class="table-pagination-footer" *ngIf="totalOrders > 0">
            <div class="pagination-info">
              <span class="pagination-count">
                Showing <strong>{{ orders.length }}</strong> of <strong>{{ totalOrders }}</strong> orders
              </span>
              <span class="pagination-badge" *ngIf="totalPages > 1">
                Page {{ currentPage }} of {{ totalPages }}
              </span>
            </div>

            <div class="page-buttons" *ngIf="totalPages > 1">
              <button class="page-btn nav-btn" [disabled]="currentPage <= 1" (click)="goToPage(1)" title="First Page">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><polyline points="11 17 6 12 11 7"/><polyline points="18 17 13 12 18 7"/></svg>
              </button>
              <button class="page-btn nav-btn" [disabled]="currentPage <= 1" (click)="prevPage()" title="Previous Page">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><polyline points="15 18 9 12 15 6"/></svg>
              </button>

              <ng-container *ngFor="let p of getPageNumbers()">
                <span *ngIf="p === '...'" class="page-ellipsis">…</span>
                <button *ngIf="p !== '...'"
                        class="page-btn"
                        [class.active]="p === currentPage"
                        (click)="goToPage(p)">
                  {{ p }}
                </button>
              </ng-container>

              <button class="page-btn nav-btn" [disabled]="currentPage >= totalPages" (click)="nextPage()" title="Next Page">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><polyline points="9 18 15 12 9 6"/></svg>
              </button>
              <button class="page-btn nav-btn" [disabled]="currentPage >= totalPages" (click)="goToPage(totalPages)" title="Last Page">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"><polyline points="13 17 18 12 13 7"/><polyline points="6 17 11 12 6 7"/></svg>
              </button>
            </div>
          </div>
        </section>

      </main>
    </div>
  `,
  styles: [`
    .order-number-link {
      font-weight: 700;
      color: var(--accent-gold);
      font-size: 13px;
      text-decoration: none;
      transition: color 0.2s ease;
    }
    .order-number-link:hover {
      color: var(--accent-gold-light);
      text-decoration: underline;
    }
    .customer-link {
      text-decoration: none;
      display: block;
    }
    .customer-name {
      font-weight: 600;
      color: var(--text-primary);
      font-size: 13px;
      transition: color 0.2s ease;
    }
    .customer-link:hover .customer-name {
      color: var(--blue);
    }
    .customer-meta {
      font-size: 11px;
      color: var(--text-muted);
    }
    .order-total-val {
      font-weight: 700;
      color: var(--text-primary);
      font-size: 14px;
    }
    .shiprocket-tag-box {
      display: flex;
      flex-direction: column;
      gap: 2px;
    }
    .courier-sub {
      font-size: 10px;
      color: var(--text-muted);
    }
    .btn-quick-shiprocket {
      display: inline-block;
      padding: 4px 10px;
      font-size: 11px;
      font-weight: 600;
      color: #a78bfa;
      background: rgba(124, 58, 237, 0.1);
      border: 1px solid rgba(124, 58, 237, 0.25);
      border-radius: 6px;
      text-decoration: none;
      transition: all 0.2s ease;
    }
    .btn-quick-shiprocket:hover {
      background: rgba(124, 58, 237, 0.2);
      border-color: rgba(124, 58, 237, 0.4);
      transform: translateY(-1px);
    }
    .view-btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      text-decoration: none;
      font-weight: 600;
      padding: 6px 12px;
      border-radius: 8px;
    }
    .view-btn:hover {
      background: rgba(255, 255, 255, 0.12);
      border-color: rgba(201, 169, 110, 0.4);
      color: var(--accent-gold);
    }
    .quick-status-select {
      padding: 4px 8px;
      font-size: 12px;
      width: 125px;
      border-radius: 8px;
    }
  `]
})
export class OrderListComponent implements OnInit {
  orders: Order[] = [];
  stats: OrderStats | null = null;
  loading = false;
  totalOrders = 0;
  totalPages = 1;
  currentPage = 1;

  statusFilter = 'all';
  paymentFilter = 'all';
  searchQuery = '';
  private searchTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private orderService: OrderService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loadStats();
    this.loadOrders();
  }

  loadStats(): void {
    this.orderService.getOrderStats().subscribe({
      next: (s) => { this.stats = s; },
      error: (e) => console.error('Stats error', e)
    });
  }

  loadOrders(): void {
    this.loading = true;
    this.orderService.getOrders(
      this.statusFilter,
      this.paymentFilter,
      this.searchQuery,
      this.currentPage
    ).subscribe({
      next: (res) => {
        this.orders = res.orders;
        this.totalOrders = res.total;
        this.totalPages = res.pages;
        this.loading = false;
      },
      error: (e) => { console.error(e); this.loading = false; }
    });
  }

  onSearchChange(): void {
    if (this.searchTimer) clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => {
      this.currentPage = 1;
      this.loadOrders();
    }, 400);
  }

  resetFilters(): void {
    this.statusFilter = 'all';
    this.paymentFilter = 'all';
    this.searchQuery = '';
    this.currentPage = 1;
    this.loadOrders();
  }

  prevPage(): void { if (this.currentPage > 1) { this.currentPage--; this.loadOrders(); } }
  nextPage(): void { if (this.currentPage < this.totalPages) { this.currentPage++; this.loadOrders(); } }

  goToPage(page: number | string): void {
    if (typeof page !== 'number') return;
    if (page < 1 || page > this.totalPages || page === this.currentPage) return;
    this.currentPage = page;
    this.loadOrders();
  }

  getPageNumbers(): (number | string)[] {
    const total = this.totalPages;
    const current = this.currentPage;
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }
    const pages: (number | string)[] = [];
    pages.push(1);

    let start = Math.max(2, current - 1);
    let end = Math.min(total - 1, current + 1);

    if (current <= 3) {
      end = 4;
    } else if (current >= total - 2) {
      start = total - 3;
    }

    if (start > 2) {
      pages.push('...');
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < total - 1) {
      pages.push('...');
    }

    pages.push(total);
    return pages;
  }

  quickUpdateStatus(order: Order): void {
    this.orderService.updateOrderStatus(order._id, { orderStatus: order.orderStatus }).subscribe({
      next: () => {},
      error: (e) => console.error('Quick update error', e)
    });
  }

  getStatusBadgeClass(status: string): string {
    const map: Record<string, string> = {
      placed: 'badge badge-grey',
      confirmed: 'badge badge-blue',
      processing: 'badge badge-gold',
      shipped: 'badge badge-purple',
      delivered: 'badge badge-green',
      cancelled: 'badge badge-red'
    };
    return map[status] || 'badge badge-grey';
  }

  getPaymentBadgeClass(status: string): string {
    const map: Record<string, string> = {
      paid: 'badge badge-green',
      pending: 'badge badge-gold',
      failed: 'badge badge-red',
      refunded: 'badge badge-blue'
    };
    return map[status] || 'badge badge-grey';
  }
}
